/**
 * MeasureEngine - Standalone measurement interaction controller.
 *
 * Encapsulates all measurement mechanics (hover, drag, point placement, snapping,
 * measure lifecycle) without any BaseTool dependency. Can be composed into any tool
 * that needs measurement capabilities.
 *
 * MeasureToolBase wraps this with proxy getters/methods so existing subclasses
 * (Point, Distance, Area) require zero changes.
 */

import * as THREE from 'three';
import { Viewer } from '../../../viewer/viewer';
import { MeasureTools, Point, type AnnotationDomain } from '../../../utils/Measure';
import { isDoorWindowAnnotation, PointoramaAnnotation } from '../../../utils/PointoramaAnnotation';
import type { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { PointoramaPlane } from '../../../utils/PointoramaPlane';
import { SphereMarker } from '../../../utils/SphereMarker';
import { Snapshot, ToolUndoRedoManager } from '../ToolUndoRedoManager';
import { Utils } from '../../../utils';
import { getAreaPointsProperty, getDistancePointsProperty, getPointPositionProperty } from '@pointorama/potree-shared';
import type { AnnotationPoint } from '@pointorama/potree-shared';
import { getPointCloudIntersection, getEffectiveDrawingPlane, ProjectionPlaneSnapping, projectOnLine, MeasureAngleSnapping } from './shared';

type EngineEvent = { type: string; [key: string]: unknown };

/**
 * Anything a hovered sphere/edge may belong to: a measurement-family
 * annotation (basic measure, surface, door/window opening) or a room — the
 * InputHandler's hover search sweeps both `scene.measurements` and
 * `scene.rooms`.
 */
export type EditableAnnotation = PointoramaAnnotation | PointoramaRoom;

/**
 * The basic measures (point / line / area) are interchangeable for editing:
 * any basic measure tool may edit any of them. Openings (door/window) extend
 * `AreaAnnotation` but belong to the door/window tools, and rooms are a
 * separate kind entirely (not a `PointoramaAnnotation`), so both are excluded.
 * Note: surface polygons are plain `AreaAnnotation`s and thus indistinguishable
 * here — same limitation as the surface adapter's own hit-test.
 */
export function isBasicMeasurement(annotation: EditableAnnotation): boolean {
  return annotation instanceof PointoramaAnnotation && !isDoorWindowAnnotation(annotation);
}

/** Intersection result from Line2 raycasting, extends THREE.Intersection with pointOnLine */
export type LineIntersect = THREE.Intersection & { pointOnLine: THREE.Vector3 };
type PointEventFactory = (measure: PointoramaAnnotation, pointIndex: number) => EngineEvent;
type MeasureEventFactory = (measure: PointoramaAnnotation) => EngineEvent;

export interface MeasureEngineOptions {
  createAnnotation: () => PointoramaAnnotation;
  minSpheresToFinish?: number;
  /**
   * Edit ownership: only shapes whose `domain` matches are hoverable/draggable/
   * deletable by this engine. Defaults to the geometry domain of `measureType`
   * ('point' | 'distance' | 'area'). Kind-hosted tools pass their adapter's
   * domain ('room', 'door_window', ...) so e.g. a room tool can't edit openings.
   */
  editableDomain?: AnnotationDomain;
  undoManager?: ToolUndoRedoManager;
  /** When provided, the engine hides the preview marker while the user is panning the camera (dragging without a sphere). */
  isUserDragging?: () => boolean;
  /**
   * Optional notification events on point edits. Tools that need to react to
   * point moves/adds/deletes with kind-specific events (e.g. AutoRoomDetectTool
   * / ManualRoomDrawTool emit `room_move_point`) provide these. They are
   * notification-only — persistence goes through `persistPoints`. When omitted,
   * no point-edit event is dispatched.
   */
  onMovePoint?: PointEventFactory;
  onAddPoint?: PointEventFactory;
  onDeletePoint?: PointEventFactory;
  onDeleteAnnotation?: MeasureEventFactory;
  /**
   * How to persist an annotation's points. Used by live edits AND by undo/redo
   * snapshot restore, so persistence goes through ONE path. Defaults to writing
   * the generic per-type points property (`getAreaPointsProperty` /
   * `getDistancePointsProperty`). Kind-hosted tools (surface/room/door-window)
   * inject a callback that routes through their adapter's `commitEdit`, so the
   * points land in the kind's own store (e.g. rooms -> `getRoomPolygonProperty`)
   * on both live edit and undo.
   */
  persistPoints?: (id: string, type: MeasureTools, points: AnnotationPoint[]) => void;
  /**
   * Which EXISTING annotations this engine may edit — hover highlight, sphere
   * drag, edge insert, right-click point delete. The in-progress drawing is
   * always editable. Each tool restricts this to its own object kind: basic
   * measure tools pass `isBasicMeasurement`, kind-hosted subtools route to
   * their adapter's `canEditAnnotation`. Default (legacy callers): annotations
   * whose `type` matches the engine's measure type.
   */
  canEditAnnotation?: (annotation: EditableAnnotation) => boolean;
}

/** Default points persistence: write the generic per-type property. */
function defaultPersistPoints(id: string, type: MeasureTools, points: AnnotationPoint[]): void {
  // POINT stores a single position, not a points array.
  if (type === MeasureTools.POINT) {
    if (points[0]) getPointPositionProperty(id).value = { ...points[0].position };
    return;
  }
  const prop = getPointsProperty(id, type);
  if (prop) prop.value = points;
}

/** Default edit-ownership domain for the generic measure tools: their geometry. */
function defaultDomainFor(type: MeasureTools): AnnotationDomain {
  if (type === MeasureTools.DISTANCE) return 'distance';
  if (type === MeasureTools.AREA) return 'area';
  return 'point';
}

/** Structural shape of anything the engine can hover/drag (Measure or PointoramaRoom). */
type EditableCandidate = {
  visible: boolean;
  domain?: AnnotationDomain;
  type?: string;
  spheres: SphereMarker[];
  backgroundLine: THREE.Object3D;
};

// ─── Undo/Redo snapshot for annotation point edits ──────────────────────

function clonePoints(points: Point[]): Point[] {
  return points.map((p) => ({
    position: p.position.clone(),
    pointCloudId: p.pointCloudId,
    color: p.color ? new Uint8Array(p.color) : undefined,
    classification: p.classification ? new Uint8Array(p.classification) : undefined,
    intensity: p.intensity ? new Float32Array(p.intensity) : undefined,
  }));
}

function toAnnotationPoints(points: Point[]): AnnotationPoint[] {
  return points.map((p) => ({
    position: { x: p.position.x, y: p.position.y, z: p.position.z },
    pointCloudId: p.pointCloudId || null,
  }));
}

function getPointsProperty(id: string, type: MeasureTools) {
  if (type === MeasureTools.AREA) return getAreaPointsProperty(id);
  if (type === MeasureTools.DISTANCE) return getDistancePointsProperty(id);
  return null;
}

/**
 * Snapshot for in-progress drawing (currentMeasure, not yet finished).
 *
 * Stores only committed points — the trailing mouse-following preview
 * (`points[last]`) is excluded so undo doesn't restore a stale mouse position.
 * On restore, the committed points are set and a fresh preview marker is
 * appended so the next mouse-move repositions it normally.
 */
class DrawingSnapshot implements Snapshot {
  private before: Point[];
  private after: Point[];

  constructor(
    private readonly engine: MeasureEngine,
    private readonly annotationIdentifier: string,
    before: Point[],
    after: Point[],
  ) {
    this.before = clonePoints(before.slice(0, -1));
    this.after = clonePoints(after.slice(0, -1));
  }

  get annotationId(): string {
    return this.annotationIdentifier;
  }

  restore(): Snapshot {
    const measure = this.engine.currentMeasure;

    const livePreview = measure.points.length > 0 ? measure.points[measure.points.length - 1].position.clone() : null;

    measure.setPoints(clonePoints(this.before));

    const tail = livePreview ?? (this.before.length > 0 ? this.before[this.before.length - 1].position.clone() : new THREE.Vector3());
    measure.addMarker(tail);
    this.engine.markPreviewUnpositioned();

    measure.update();

    const tmp = this.before;
    this.before = this.after;
    this.after = tmp;

    return this;
  }
}

/**
 * Snapshot for point edits on a finished annotation.
 * Writes directly to the reactive property — the annotation's subscription
 * handles the visual update. This avoids touching scene objects that the
 * React sync layer also manages.
 */
class PointsSnapshot implements Snapshot {
  private readonly pointsA: string;
  private readonly pointsB: string;
  /** false = next restore applies A (undo); true = next restore applies B (redo) */
  private applyB = false;

  constructor(
    private readonly id: string,
    private readonly type: MeasureTools,
    before: AnnotationPoint[],
    after: AnnotationPoint[],
    private readonly persist: (id: string, type: MeasureTools, points: AnnotationPoint[]) => void,
  ) {
    // Store as JSON so external code can never mutate our saved state
    this.pointsA = JSON.stringify(before);
    this.pointsB = JSON.stringify(after);
  }

  restore(): Snapshot {
    // Parse fresh copy each time — no shared references
    const points = JSON.parse(this.applyB ? this.pointsB : this.pointsA) as AnnotationPoint[];
    this.persist(this.id, this.type, points);
    this.applyB = !this.applyB;
    return this;
  }
}

/**
 * Snapshot for annotation existence changes (delete/create).
 * Dispatches events that the React layer handles through the existing
 * coalesced create/delete path. Does NOT touch scene objects directly.
 */
class ExistenceSnapshot implements Snapshot {
  /** false = next restore creates (undo of delete); true = next restore deletes (redo of delete) */
  private shouldDelete = false;
  private readonly pointsJson: string;

  constructor(
    private readonly engine: MeasureEngine,
    private readonly id: string,
    private readonly type: MeasureTools,
    points: AnnotationPoint[],
  ) {
    this.pointsJson = JSON.stringify(points);
  }

  restore(): Snapshot {
    const scene = this.engine.viewer.scene;

    if (this.shouldDelete) {
      const live = scene.measurements.find((m) => m.identifier === this.id) as PointoramaAnnotation | undefined;
      if (live) {
        scene.removeMeasurement(live);
      }
      scene.dispatchEvent({
        type: 'measurement_delete',
        value: { measure: { identifier: this.id } },
      });
    } else {
      const points = JSON.parse(this.pointsJson) as AnnotationPoint[];
      const prop = getPointsProperty(this.id, this.type);
      if (prop) prop.value = points;

      scene.dispatchEvent({
        type: 'measurement_finish',
        value: {
          measure: {
            identifier: this.id,
            type: this.type,
            points: points.map((p) => ({
              position: new THREE.Vector3(p.position.x, p.position.y, p.position.z),
              pointCloudId: p.pointCloudId || undefined,
            })),
          },
        },
      });
    }

    this.shouldDelete = !this.shouldDelete;
    return this;
  }
}

export class MeasureEngine {
  // --- Config ---
  readonly measureType: MeasureTools;
  /** Edit ownership — see MeasureEngineOptions.editableDomain. */
  readonly editableDomain: AnnotationDomain;
  drawingPlane: PointoramaPlane | null = null;
  minSpheresToFinish: number;

  // --- State ---
  currentMeasure!: PointoramaAnnotation;
  hoveredSphere?: SphereMarker;
  hoveredLineIntersect?: LineIntersect;
  draggedSphere?: SphereMarker;
  draggingEdge = false;
  lastMovedMeasure: PointoramaAnnotation | null = null;
  private _previewPositioned = false;

  // --- Modules ---
  planeSnapping!: ProjectionPlaneSnapping;
  angleSnappingModule!: MeasureAngleSnapping;

  // --- Event factories (default to measurement_* events, tools can override) ---
  readonly onMovePoint: PointEventFactory;
  readonly onAddPoint: PointEventFactory;
  readonly onDeletePoint: PointEventFactory;
  readonly onDeleteAnnotation: MeasureEventFactory;

  // --- Private ---
  readonly viewer: Viewer;
  private readonly createAnnotation: () => PointoramaAnnotation;
  private readonly undoManager: ToolUndoRedoManager | null;
  private readonly isUserDragging: (() => boolean) | null;
  /** Which existing annotations may be edited. See options. */
  private readonly canEditAnnotation: (annotation: EditableAnnotation) => boolean;
  /** Points persistence used by live edits + snapshot restore. See options. */
  readonly persistPoints: (id: string, type: MeasureTools, points: AnnotationPoint[]) => void;
  private dragDrawingPlane: PointoramaPlane | null = null;
  private editableCollection?: { domain?: AnnotationDomain; spheres: SphereMarker[] }[];
  private preDragPosition: THREE.Vector3 | null = null;
  private preDragAnnotation: PointoramaAnnotation | null = null;
  private preDragPoints: { drawing: Point[] } | { finished: AnnotationPoint[] } | null = null;

  get measureScene(): THREE.Scene {
    return this.viewer.measurementScene;
  }

  constructor(viewer: Viewer, measureType: MeasureTools, options: MeasureEngineOptions) {
    this.viewer = viewer;
    this.measureType = measureType;
    this.editableDomain = options.editableDomain ?? defaultDomainFor(measureType);
    this.createAnnotation = options.createAnnotation;
    this.minSpheresToFinish = options.minSpheresToFinish ?? 2;
    this.undoManager = options.undoManager ?? null;
    this.isUserDragging = options.isUserDragging ?? null;
    this.canEditAnnotation = options.canEditAnnotation ?? ((annotation) => annotation.type === measureType);
    this.persistPoints = options.persistPoints ?? defaultPersistPoints;

    this.onMovePoint =
      options.onMovePoint ??
      ((measure, pointIndex) => ({
        type: 'measurement_move_point',
        value: { pointIndex, point: measure.points[pointIndex], measure },
      }));
    this.onAddPoint =
      options.onAddPoint ??
      ((measure, pointIndex) => ({
        type: 'measurement_add_point',
        value: { pointIndex, point: measure.points[pointIndex], measure },
      }));
    this.onDeletePoint =
      options.onDeletePoint ??
      ((measure, pointIndex) => ({
        type: 'measurement_delete_point',
        value: { pointIndex, measure },
      }));
    this.onDeleteAnnotation =
      options.onDeleteAnnotation ??
      ((measure) => ({
        type: 'measurement_delete',
        value: { measure },
      }));
  }

  // =========================================================================
  // Lifecycle
  // =========================================================================

  init(): void {
    // Init projection plane snapping (hover, click, snap, helper line)
    this.planeSnapping = new ProjectionPlaneSnapping(this.viewer);
    this.planeSnapping.init(this.measureScene);

    // Init angle snapping module (owns its own helper line)
    this.angleSnappingModule = new MeasureAngleSnapping();
    this.angleSnappingModule.init(this.measureScene);

    this.startMeasure();
    this.setMarkerSizes();

    // Reposition/rescale the preview when the camera changes without a mouse move
    // (e.g. scroll-zoom). Without this, the preview point keeps its old world position
    // and label scale until the next mousemove re-runs drawNewMeasure.
    this.viewer.addEventListener('camera_changed', this.handleCameraChanged);
  }

  dispose(): void {
    this.viewer.removeEventListener('camera_changed', this.handleCameraChanged);

    // Clean up current measure
    this.measureScene.remove(this.currentMeasure);

    // Clean up state
    this.resetHoverState();
    this.resetDragState();
    this.angleSnappingModule.dispose(this.measureScene);
    this.planeSnapping.dispose(this.measureScene);

    // Reset marker sizes — nothing is editable once the engine is gone
    this.resetMarkerSizes();
  }

  /** Shrink all markers (owned included) back to the inactive scale. */
  private resetMarkerSizes(): void {
    const items = this.editableCollection ?? [...this.viewer.scene.measurements, ...(this.editableDomain === 'room' ? this.viewer.scene.rooms : [])];
    // Persist the size for spheres created after this call (e.g. annotations
    // synced from the server while no measure tool is active) — only when
    // operating on the default collection.
    if (!this.editableCollection) this.viewer.scene.measurementSphereScale = 0.5;
    for (const item of items) {
      for (const sphere of item.spheres) {
        sphere.setScaleFactor(0.5);
      }
    }
  }

  private handleCameraChanged = (): void => {
    // Don't reposition while the user is dragging an existing sphere — the drag
    // handler owns positioning in that case.
    if (this.isDraggingSphere()) return;

    // Don't reposition while panning the camera — the preview should stay frozen in place.
    if (this.isCameraPanning()) return;

    const mouse = this.viewer.inputHandler.mouse;
    this.drawNewMeasure(mouse.clone());
  };

  // =========================================================================
  // Orbit control helpers
  // =========================================================================

  /** Returns true when hovering a draggable sphere/edge and not drawing. */
  shouldBlockOrbitControls(): boolean {
    const couldDragSphere = this.canEdit(this.hoveredSphere?.parent);
    const couldDragEdge = this.canEdit(this.hoveredLineIntersect?.object?.parent);
    return (couldDragSphere || couldDragEdge) && !this.drawingInProgress();
  }

  /**
   * True when this engine may edit the object owning a hovered sphere/line.
   * The in-progress drawing is always editable; everything else must BOTH
   * belong to this engine's edit domain (hover candidates are gathered
   * domain-scoped already — see getEditableCandidates) AND pass the tool's
   * finer `canEditAnnotation` restriction (rooms for the room tools,
   * openings for door/window, basic measures for the measure tools, …).
   */
  private canEdit(obj: THREE.Object3D | null | undefined): boolean {
    if (!obj) return false;
    if (obj === (this.currentMeasure as unknown)) return true; // own in-progress drawing
    if ((obj as { domain?: AnnotationDomain }).domain !== this.editableDomain) return false;
    return this.canEditAnnotation(obj as EditableAnnotation);
  }

  /**
   * All shapes this engine may hover/drag: measurements of the engine's domain,
   * plus rooms for room-domain engines (rooms live in scene.rooms, not
   * scene.measurements). This replaces InputHandler's untyped gathering, which
   * offered every AREA-shaped object (rooms, openings, surfaces) to every tool.
   */
  private getEditableCandidates(): EditableCandidate[] {
    const measures: EditableCandidate[] = this.viewer.scene.measurements.filter((m) => m.visible && !m.isCADMeasure && m.domain === this.editableDomain);
    if (this.editableDomain === 'room') {
      return [...measures, ...this.viewer.scene.rooms.filter((r) => r.visible)];
    }
    return measures;
  }

  private getHoveredSphereInternal(excluded: unknown[]): SphereMarker | undefined {
    const spheres = this.getEditableCandidates()
      .filter((m) => !excluded.includes(m))
      .flatMap((m) => m.spheres)
      .map((s) => s.mesh);
    const intersects = this.viewer.inputHandler.raycaster.intersectObjects(spheres);
    return intersects.at(0)?.object.parent as SphereMarker | undefined;
  }

  private getHoveredLineIntersectInternal(excluded: unknown[]): LineIntersect | undefined {
    const lines = this.getEditableCandidates()
      .filter((m) => !excluded.includes(m))
      // POINT is geometry (no line to hover), not ownership — keep keyed on type.
      .flatMap((m) => (m.type === 'POINT' ? [] : [m.backgroundLine]));
    const intersects = this.viewer.inputHandler.raycaster.intersectObjects(lines);
    return intersects.at(0) as LineIntersect | undefined;
  }

  // =========================================================================
  // Hover state
  // =========================================================================

  updateHoverState(): void {
    this.planeSnapping.updateHover();
    this.setHoveredSphere();
    this.setHoveredLineIntersect();
  }

  setHoveredSphere(): void {
    const previousHoveredSphere = this.hoveredSphere;
    this.hoveredSphere = this.getHoveredSphereInternal([this.draggedSphere?.parent]);
    if (previousHoveredSphere && previousHoveredSphere !== this.hoveredSphere) {
      previousHoveredSphere.unHighlight();
    }
    if (this.canEdit(this.hoveredSphere?.parent) && !this.draggedSphere) {
      this.hoveredSphere?.highlight();
    }
  }

  setHoveredLineIntersect(): void {
    this.hoveredLineIntersect = this.getHoveredLineIntersectInternal([this.draggedSphere?.parent]);
  }

  private resetHoverState(): void {
    this.hoveredSphere = undefined;
    this.hoveredLineIntersect = undefined;
    Utils.changeCursor('default');
  }

  // =========================================================================
  // Drag state
  // =========================================================================

  isDraggingSphere(): boolean {
    return !!this.draggedSphere;
  }

  /** True when the user is dragging the camera (left-click pan), not a sphere/edge. */
  private isCameraPanning(): boolean {
    return !!this.isUserDragging?.() && !this.isDraggingSphere();
  }

  /**
   * Called on first drag movement. Determines if we're dragging a sphere or edge.
   */
  onFirstDragDetected(): void {
    if (this.drawingInProgress()) return;

    if (this.hoveredSphere && this.canEdit(this.hoveredSphere.parent)) {
      const annotation = this.hoveredSphere.parent as PointoramaAnnotation;
      const index = annotation.spheres.indexOf(this.hoveredSphere);
      // Capture pre-drag state; snapshot is pushed at drag end (so no-op drags don't clear redo)
      this.preDragAnnotation = annotation;
      this.preDragPoints = annotation === this.currentMeasure && !annotation.finished ? { drawing: clonePoints(annotation.points) } : { finished: toAnnotationPoints(annotation.points) };
      this.preDragPosition = annotation.points[index].position.clone();
      this.draggedSphere = this.hoveredSphere;
      this.draggingEdge = false;
      this.createDragDrawingPlane(this.hoveredSphere.getWorldPosition(new THREE.Vector3()));
    } else if (this.hoveredLineIntersect && this.canEdit(this.hoveredLineIntersect.object?.parent)) {
      this.startDraggingEdge(this.hoveredLineIntersect);
    } else {
      this.draggedSphere = undefined;
      this.draggingEdge = false;
    }
  }

  /** Insert a new point on an edge and start dragging it. */
  startDraggingEdge(lineIntersect: LineIntersect): void {
    const pointIndex = (lineIntersect.faceIndex ?? 0) + 1;
    const hoveredMeasure = lineIntersect.object.parent as PointoramaAnnotation | null;
    if (!hoveredMeasure) return;

    // Capture pre-insert state
    this.preDragAnnotation = hoveredMeasure;
    this.preDragPoints = hoveredMeasure === this.currentMeasure && !hoveredMeasure.finished ? { drawing: clonePoints(hoveredMeasure.points) } : { finished: toAnnotationPoints(hoveredMeasure.points) };
    this.preDragPosition = lineIntersect.pointOnLine.clone();

    hoveredMeasure.addMarker(lineIntersect.pointOnLine, pointIndex);
    hoveredMeasure.spheres[pointIndex].highlight();

    this.draggingEdge = true;
    hoveredMeasure.needsUpdate = true;

    this.draggedSphere = hoveredMeasure.spheres[pointIndex];
    this.createDragDrawingPlane(lineIntersect.pointOnLine);
  }

  /** Move the dragged sphere to the new position. */
  onDragPoint(location: THREE.Vector3): void {
    if (!this.draggedSphere) return;

    const measure = this.draggedSphere.parent as PointoramaAnnotation;
    const index = measure.spheres.indexOf(this.draggedSphere);
    measure.setPointData(index, location);
  }

  /** Finish dragging. Push snapshot only if the point actually moved. */
  onDragPointEnd(): void {
    if (!this.draggedSphere) return;

    const measure = this.draggedSphere.parent as PointoramaAnnotation;
    const index = measure.spheres.indexOf(this.draggedSphere);

    if (index === -1) return;

    const moved = !this.preDragPosition || !measure.points[index].position.equals(this.preDragPosition);

    if (moved && this.preDragAnnotation && this.preDragPoints && this.undoManager) {
      // Push before+after snapshot
      if ('drawing' in this.preDragPoints) {
        this.undoManager.pushState(new DrawingSnapshot(this, measure.identifier, this.preDragPoints.drawing, clonePoints(measure.points)));
      } else {
        this.undoManager.pushState(new PointsSnapshot(measure.identifier, measure.type as MeasureTools, this.preDragPoints.finished, toAnnotationPoints(measure.points), this.persistPoints));
      }
      const event = this.draggingEdge ? this.onAddPoint(measure, index) : this.onMovePoint(measure, index);
      this.viewer.scene.dispatchEvent(event);
    }

    this.preDragPosition = null;
    this.preDragAnnotation = null;
    this.preDragPoints = null;
    this.lastMovedMeasure = measure;
    measure.lastMovedPoint = index;
    this.draggingEdge = false;
    this.draggedSphere = undefined;
  }

  /** Remove a marker from a measurement (right-click on sphere). */
  removeMarker(measure: PointoramaAnnotation, pointIndex: number): void {
    // Callers pass whatever the hovered sphere belongs to — enforce the
    // per-tool edit restriction here so every right-click path is covered.
    if (!this.canEdit(measure)) return;
    if (measure.canRemoveMarker()) {
      this.withUndo(measure, () => {
        measure.removeMarker(pointIndex);
        this.viewer.scene.dispatchEvent(this.onDeletePoint(measure, pointIndex));
      });
    } else {
      // Full annotation deletion — push existence snapshot, then delete
      this.pushDeleteUndo(measure);
      this.viewer.scene.removeMeasurement(measure);
      this.viewer.scene.dispatchEvent(this.onDeleteAnnotation(measure));
    }
    // Hover only refreshes on mouse move; drop it so a repeat right-click
    // doesn't act on the removed point/annotation again.
    this.hoveredSphere?.unHighlight();
    this.resetHoverState();
  }

  /**
   * Create a temporary drawing plane at the dragged point's elevation for depth preservation.
   * Uses the tool's drawingPlane normal when available (e.g. room floor plane), otherwise
   * falls back to a view-aligned plane. Positioned at the point to preserve its elevation.
   *
   * Note: If a tool edits objects that belong to a constrained plane (e.g. rooms) without
   * setting the correct drawingPlane, points could be dragged off-plane. Currently all tools
   * that edit room/surface points set drawingPlane correctly, so this is not an issue.
   */
  private createDragDrawingPlane(pointPosition: THREE.Vector3): void {
    const viewPlane = getEffectiveDrawingPlane(this.viewer);
    if (!viewPlane) return;

    this.dragDrawingPlane = new PointoramaPlane({
      position: pointPosition.clone(),
      normal: viewPlane.getNormal(),
      visible: false,
    });
  }

  private resetDragState(): void {
    this.draggedSphere = undefined;
    this.draggingEdge = false;
    this.dragDrawingPlane = null;
  }

  // =========================================================================
  // Undo/Redo
  // =========================================================================

  /** Undo only if the top snapshot belongs to the current in-progress drawing. */
  undoCurrentMeasure(): boolean {
    if (!this.undoManager) return false;
    const top = this.undoManager.peekUndo();
    if (!(top instanceof DrawingSnapshot)) return false;
    if (top.annotationId !== this.currentMeasure?.identifier) return false;
    return this.undoManager.undo();
  }

  /**
   * Run `mutate` and push a snapshot capturing before+after state.
   * For drawings: captures point arrays directly.
   * For finished annotations: captures AnnotationPoints for the reactive property.
   */
  private withUndo(annotation: PointoramaAnnotation, mutate: () => void): void {
    if (!this.undoManager) {
      mutate();
      return;
    }

    const isDrawing = annotation === this.currentMeasure && !annotation.finished;
    if (isDrawing) {
      const before = clonePoints(annotation.points);
      mutate();
      const after = clonePoints(annotation.points);
      this.undoManager.pushState(new DrawingSnapshot(this, annotation.identifier, before, after));
    } else {
      const before = toAnnotationPoints(annotation.points);
      mutate();
      const after = toAnnotationPoints(annotation.points);
      this.undoManager.pushState(new PointsSnapshot(annotation.identifier, annotation.type as MeasureTools, before, after, this.persistPoints));
    }
  }

  /**
   * Push an existence snapshot for annotation deletion.
   * Captures the annotation's current points so undo can recreate it.
   */
  private pushDeleteUndo(annotation: PointoramaAnnotation): void {
    if (!this.undoManager) return;
    this.undoManager.pushState(new ExistenceSnapshot(this, annotation.identifier, annotation.type as MeasureTools, toAnnotationPoints(annotation.points)));
  }

  // =========================================================================
  // Point resolution
  // =========================================================================

  /**
   * Get the snapped point position under the mouse cursor.
   * Handles plane-based (2D mode) and 3D point cloud raycast paths.
   */
  getPointUnderMouse(mouse: THREE.Vector2, angleSnapping = false): THREE.Vector3 | null {
    const effectiveDrawingPlane = getEffectiveDrawingPlane(this.viewer, this.dragDrawingPlane ?? this.drawingPlane);

    if (effectiveDrawingPlane) {
      return this.getPointOnPlane(effectiveDrawingPlane, angleSnapping);
    }

    return this.getPointFrom3DRaycast(mouse, angleSnapping);
  }

  /** Plane-based point resolution. Used when a drawing plane is set or in 2D mode. */
  private getPointOnPlane(drawingPlane: PointoramaPlane, angleSnapping: boolean): THREE.Vector3 | null {
    const intersect = this.viewer.inputHandler.getIntersectWithPlane(drawingPlane);
    if (!intersect) return null;

    // Snap to existing sphere — project onto the drawing plane so we keep the
    // plane's elevation (e.g. Z=0 in a 2D top view) rather than lifting off it.
    if (this.hoveredSphere && this.canEdit(this.hoveredSphere.parent)) {
      return drawingPlane.getPointProjection(this.hoveredSphere.getWorldPosition(new THREE.Vector3()));
    }
    // Snap to existing line — project onto the drawing plane for the same reason.
    if (this.hoveredLineIntersect && this.canEdit(this.hoveredLineIntersect.object?.parent)) {
      return drawingPlane.getPointProjection(this.hoveredLineIntersect.pointOnLine);
    }
    // Snap to first point for polygon closing
    if (this.hoveringFirstPoint()) {
      return this.currentMeasure.points[0].position.clone();
    }
    // Angle snapping (90° constraint)
    if (angleSnapping) {
      if (this.draggedSphere) {
        // Drag-specific: maintain 90° at adjacent corners
        const snapped = this.angleSnappingModule.getSnappedAnglePointForDrag(drawingPlane, intersect, this.draggedSphere, this.viewer.scene.getActiveCamera());
        if (snapped) return snapped;
      } else {
        // Drawing: snap new point to 90° relative to previous segments
        const snapped = this.angleSnappingModule.computeSnappedAnglePoint(drawingPlane, intersect, this.currentMeasure.points, (origin, direction) => projectOnLine(intersect, origin, direction), this.viewer.scene.getActiveCamera());
        if (snapped) return snapped;
      }
    }

    return intersect;
  }

  /** 3D point cloud raycast path. Used when no drawing plane is available. */
  private getPointFrom3DRaycast(mouse: THREE.Vector2, angleSnapping: boolean): THREE.Vector3 | null {
    const location = getPointCloudIntersection(this.viewer, mouse);
    if (!location) return null;

    if (this.planeSnapping.hasPlanes()) {
      const previousPoint = this.currentMeasure.points.at(-2)?.position;
      const snapped = this.planeSnapping.snap(location, previousPoint);

      // Angle snapping requires the snapped point and previous point to be on the plane
      if (angleSnapping && !this.planeSnapping.lastSnapWasPerpendicular) {
        const plane = this.planeSnapping.getSinglePlane();
        if (plane && (!previousPoint || plane.containsPoint(previousPoint))) {
          if (this.draggedSphere) {
            const angleSnapped = this.angleSnappingModule.getSnappedAnglePointForDrag(plane, snapped, this.draggedSphere, this.viewer.scene.getActiveCamera());
            if (angleSnapped) return angleSnapped;
          } else {
            const angleSnapped = this.angleSnappingModule.computeSnappedAnglePoint(plane, snapped, this.currentMeasure.points, (origin, direction) => projectOnLine(snapped, origin, direction), this.viewer.scene.getActiveCamera());
            if (angleSnapped) return angleSnapped;
          }
        }
      }

      return snapped;
    }

    if (angleSnapping) {
      return this.angleSnappingModule.snapToHorizontalOrVertical(location, this.currentMeasure.points);
    }

    return this.findSnappingPoint(location) ?? location;
  }

  /** Update the preview point position for the current measure */
  drawNewMeasure(mouse: THREE.Vector2, angleSnapping = false): void {
    // Reset before point resolution — snapping methods will re-enable if active
    this.angleSnappingModule.resetHelperLine();

    // Freeze preview in place while panning the camera
    if (this.isCameraPanning()) return;

    const location = this.getPointUnderMouse(mouse, angleSnapping);
    if (location) {
      this.currentMeasure.setPositionOfLastPoint(location);
      this._previewPositioned = true;
      this.currentMeasure.visible = true;
    }

    this.currentMeasure.update();
  }

  /** Find snapping point in 3D mode (first-point closing, sphere, perpendicular line snap) */
  private findSnappingPoint(pointCloudHit: THREE.Vector3): THREE.Vector3 | null {
    // Closing snap: hover first point of polygon
    if (this.hoveringFirstPoint()) {
      return this.currentMeasure.points[0].position.clone();
    }

    if (this.hoveredSphere && this.canEdit(this.hoveredSphere.parent)) {
      return this.hoveredSphere.getWorldPosition(new THREE.Vector3());
    }

    if (this.hoveredLineIntersect) {
      // Skip line snap if the snap point is behind the pointcloud surface.
      // This prevents snapping to lines that are occluded by the pointcloud.
      const snapPoint = this.currentMeasure.points.length < 2 ? this.hoveredLineIntersect.pointOnLine : this.snapToPerpendicular(this.hoveredLineIntersect);

      const camera = this.viewer.scene.getActiveCamera();
      const cameraPos = camera.position;
      const distToPointCloud = cameraPos.distanceTo(pointCloudHit);
      const distToSnapPoint = cameraPos.distanceTo(snapPoint);

      // Allow a small tolerance so lines on or near the surface still snap
      if (distToSnapPoint > distToPointCloud * 1.1) {
        return null;
      }

      return snapPoint;
    }

    return null;
  }

  // =========================================================================
  // Shared utility methods
  // =========================================================================

  /** Create a new annotation and add to scene */
  startMeasure(): void {
    this.measureScene.remove(this.currentMeasure);

    this.currentMeasure = this.createAnnotation();

    this.currentMeasure.addMarker(new THREE.Vector3());
    this.currentMeasure.visible = false;
    this.currentMeasure.update();
    this.measureScene.add(this.currentMeasure);
    this._previewPositioned = false;

    this.lastMovedMeasure = null;
  }

  /** Whether drawing is in progress (more than the initial preview point) */
  drawingInProgress(): boolean {
    return this.currentMeasure.points.length > 1;
  }

  /** Whether the preview marker has been positioned by drawNewMeasure() since it was last created */
  canPlacePoint(): boolean {
    return this._previewPositioned;
  }

  /** Reset preview-positioned flag so a click won't place a point until the mouse moves. */
  markPreviewUnpositioned(): void {
    this._previewPositioned = false;
  }

  /** Place the current preview point and create a new trailing preview marker */
  placePoint(): void {
    const measure = this.currentMeasure;
    this.withUndo(measure, () => {
      measure.addMarker(measure.points[measure.points.length - 1].position.clone());
    });
    this._previewPositioned = false;
  }

  /** Set sphere marker sizes based on edit ownership. Pass a collection to
   *  override the default (measurements + rooms for room-domain engines). */
  setMarkerSizes(collection?: { domain?: AnnotationDomain; spheres: SphereMarker[] }[]): void {
    if (collection) this.editableCollection = collection;
    const items = this.editableCollection ?? [...this.viewer.scene.measurements, ...(this.editableDomain === 'room' ? this.viewer.scene.rooms : [])];
    // Persist the size for spheres created after this call (e.g. annotations
    // synced from the server while no measure tool is active) — only when
    // operating on the default collection.
    if (!this.editableCollection) this.viewer.scene.measurementSphereScale = 1;
    for (const item of items) {
      const isActive = item.domain === this.editableDomain;
      for (const sphere of item.spheres) {
        sphere.setScaleFactor(isActive ? 1 : 0.5);
      }
    }
  }

  /** Update the current measure visibility (hide when hovering editable spheres or dragging) */
  updateCurrentMeasureVisibility(): void {
    if ((this.hoveredSphere && this.canEdit(this.hoveredSphere.parent) && !this.drawingInProgress()) || this.isDraggingSphere()) {
      this.currentMeasure.visible = false;
    }
  }

  /** Combined visual update */
  updateVisuals(): void {
    this.planeSnapping.updateHelperLine();
    this.updateCurrentMeasureVisibility();
  }

  /** Convert a MouseEvent to screen coordinates relative to the renderer */
  getMouseFromEvent(event: MouseEvent): THREE.Vector2 {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  // =========================================================================
  // Multi-point measurement
  // =========================================================================

  /**
   * Complete the current measurement. Removes trailing preview marker and returns
   * the completed annotation, or null if not enough points. Always restarts a fresh measure.
   *
   * The consuming tool decides what to do with the returned annotation:
   * - MeasureToolBase: `viewer.scene.finishMeasurement(completed)` (standard annotation)
   * - ManualRoomDrawTool: room detection + create_room event
   * - ManualSurfaceDrawTool: surface measurement event
   */
  completeCurrentMeasurement(): PointoramaAnnotation | null {
    let completed: PointoramaAnnotation | null = null;

    if (this.currentMeasure.spheres.length > this.minSpheresToFinish) {
      this.currentMeasure.removeMarker(this.currentMeasure.points.length - 1);
      completed = this.currentMeasure;
    }

    // Drawing undo states are no longer relevant once the annotation is finished
    this.undoManager?.clear();

    this.startMeasure();
    return completed;
  }

  /** Whether the mouse is hovering the first point of the current measure (for polygon closing) */
  hoveringFirstPoint(): boolean {
    if (this.currentMeasure.spheres.length < 4) return false;
    const firstSphere = this.currentMeasure.spheres[0];
    return !!this.viewer.inputHandler.isHovered(firstSphere);
  }

  /** Snap to perpendicular on a hovered line segment relative to the previous point */
  snapToPerpendicular(lineIntersect: LineIntersect): THREE.Vector3 {
    const previousPoint = this.currentMeasure.points.at(-2);
    if (!previousPoint) return lineIntersect.pointOnLine;

    const hoveredMeasure = lineIntersect.object.parent as PointoramaAnnotation;
    const faceIndex = lineIntersect.faceIndex ?? 0;
    const perpendicularPoint = hoveredMeasure.getClosestPointOnLineSegment(faceIndex, previousPoint.position);

    const cosTheta = previousPoint.position.distanceTo(perpendicularPoint) / previousPoint.position.distanceTo(lineIntersect.pointOnLine);

    if (1 - cosTheta < 0.005) {
      return perpendicularPoint;
    }
    return lineIntersect.pointOnLine;
  }
}
